import { FC, useEffect, useState } from "react";
import ReactDatePicker, { ReactDatePickerProps } from "react-datepicker";
import _ from "lodash";
import {
  ActivityIndicator,
  ImageURISource,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  ViewStyle,
} from "react-native";
import * as DocumentPicker from "expo-document-picker";

import { kColors } from "src/kColors";

import { SLImage, tReactiveImageProps } from "../ReactiveImage";
import { Import_Sm } from "../impComponents";
import { PdClose } from "../Icons";

export type tPdImagePickerAction = "change" | "remove";

export type tImagePickerValue = {
  uri?: string;
  file?: File;
  action: tPdImagePickerAction;
};

export type tPdImagePicker = Omit<tReactiveImageProps, "style" | "source"> & {
  style?: StyleProp<ViewStyle> | undefined;
  source?: ImageURISource;
  onChange?: (value: tImagePickerValue) => void;
  placeholder?: string;
  showPlaceholder?: boolean;
  allowDelete?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
};

import "src/util/datePicker.css";
import { kStyleConst } from "src/kStyle";

export const DateRangePicker: FC<ReactDatePickerProps> = (props) => {
  return <ReactDatePicker selectsRange inline {...props} />;
};

export const PdImagePicker: FC<tPdImagePicker> = ({
  style,
  source,
  onChange,
  placeholder,
  showPlaceholder = true,
  allowDelete,
  isLoading,
  isDisabled,
  ...rest
}) => {
  const [_source, setSource] = useState<ImageURISource>(source || {});
  const isControlled = !!source;

  const handleOnPress = async () => {
    if (!isDisabled) {
      const image = await DocumentPicker.getDocumentAsync({ type: "image/png, image/jpeg, image/webp" });

      if (image.type === "success") {
        if (isControlled) {
          setSource({ uri: image.uri });
        }
        onChange?.({ uri: image.uri, file: image.file, action: "change" });
      }
    }
  };

  const handleOnPressDelete = () => {
    if (isControlled) {
      setSource({});
    }
    onChange?.({ uri: undefined, file: undefined, action: "remove" });
  };

  const renderActionButton = () => {
    if (isLoading) {
      return <ActivityIndicator size="small" color={kColors.brandDark} style={styles.actionButton} />;
    }

    if (allowDelete && _source.uri) {
      return (
        <TouchableOpacity onPress={handleOnPressDelete} style={styles.actionButton}>
          <PdClose style={styles.removeButtonIcon} color={kColors.brandDark} />
        </TouchableOpacity>
      );
    }

    return null;
  };

  useEffect(() => {
    if (source?.uri && !_source.uri) {
      setSource(source);
    }
  }, [_source, source]);

  return (
    <TouchableOpacity disabled={isDisabled} onPress={handleOnPress} style={[isDisabled ? styles.opacityMd : {}, style]}>
      {renderActionButton()}
      {!_source.uri ? (
        <>
          <Import_Sm style={styles.uploadIcon} />
          {showPlaceholder && <Text style={styles.uploadLabel}>{placeholder || "Upload Image"}</Text>}
        </>
      ) : (
        <SLImage source={_source} {...rest} />
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  actionButton: {
    padding: 2 * kStyleConst.kSpacing,
    position: "absolute",
    right: 0,
    top: 0,
    zIndex: 10,
  },
  opacityMd: {
    opacity: 0.4,
  },
  removeButtonIcon: {
    height: 7,
    width: 7,
  },
  uploadIcon: {
    ...kStyleConst.kLegendStamp,
    marginHorizontal: kStyleConst.kSpacing * 2,
    marginVertical: 5,
  },
  uploadLabel: {
    fontFamily: "Poppins",
    fontSize: 10,
    textAlign: "center",
  },
});
